import { useEffect, useRef, useState } from "react";
import { clientLogger } from "~/logger.client";

type UseScriptOptions = {
	// Defer injection until the gating data exists (mirrors `authDetails`/`paymentSession`). Default true.
	enabled?: boolean;
	// When provided, readiness is detected by polling this predicate for the SDK's `window` global
	// rather than the script's `load` event. Use it for SDKs that assign their global a tick after load
	// (and where a pre-existing `<script>` may have fired `load` before this hook ran).
	isReady?: () => boolean;
	// Datadog log group for load/timeout failures, e.g. "SPREEDLY_SCRIPT_LOAD_ERROR".
	logGroup: string;
	pollIntervalMs?: number;
	maxAttempts?: number;
};

/**
 * Injects an external `<script>` once (deduped by `src`) and reports when it is usable. Two readiness
 * modes: poll a `window`-global predicate (`isReady`), or — with no predicate — track the script's
 * `load` event, recording the outcome in a `data-status` attribute so a remount reads the tag's real
 * state instead of assuming a pre-existing tag has finished loading. Listeners and the poll timer are
 * cleaned up on unmount, so no state is set on an unmounted component and nothing leaks.
 */
export const useScript = (url: string, options: UseScriptOptions): boolean => {
	const { enabled = true, isReady: readyProbe, logGroup, pollIntervalMs = 50, maxAttempts = 200 } = options;
	const [isReady, setIsReady] = useState(false);

	// Keep the probe in a ref so an inline `() => !!window.X` arrow doesn't re-run the effect each render.
	const readyProbeRef = useRef(readyProbe);
	readyProbeRef.current = readyProbe;
	const usesProbe = readyProbe != null;

	useEffect(() => {
		// Clear any readiness carried over from a previous `url`/`enabled` before re-deriving it.
		setIsReady(false);

		if (!enabled) {
			return;
		}

		let cancelled = false;
		const markReady = () => {
			if (!cancelled) {
				setIsReady(true);
			}
		};
		const logFailure = (message: string) => clientLogger.error(message, new Error(message), { logGroup });

		const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);

		// Poll-for-global mode: inject if absent, then poll the predicate. Robust to a pre-existing,
		// already-loaded tag because readiness is the global itself, not the load event.
		if (usesProbe) {
			if (!existing) {
				const script = document.createElement("script");
				script.src = url;
				script.async = true;
				script.onerror = () => logFailure(`Failed to load script: ${url}`);
				document.body.appendChild(script);
			}

			const probe = () => readyProbeRef.current?.() ?? false;
			if (probe()) {
				markReady();
				return () => {
					cancelled = true;
				};
			}

			let attempts = 0;
			const interval = window.setInterval(() => {
				if (probe()) {
					window.clearInterval(interval);
					markReady();
				} else if (++attempts >= maxAttempts) {
					window.clearInterval(interval);
					if (!cancelled) {
						logFailure(`Script did not initialize in time: ${url}`);
					}
				}
			}, pollIntervalMs);

			return () => {
				cancelled = true;
				window.clearInterval(interval);
			};
		}

		// Load-event mode: readiness is the script's `load` event, tracked via a `data-status` attribute
		// so a remount (or another hook instance) reads the real state instead of assuming "loaded".
		const onLoad = () => markReady();
		const onError = () => logFailure(`Failed to load script: ${url}`);

		let script = existing;
		if (script) {
			const status = script.dataset.status;
			if (status === "ready" || status === undefined) {
				// Confirmed loaded, or an untracked tag we assume is loaded (avoids getting stuck if its
				// load event already fired before this hook attached a listener).
				markReady();
			} else if (status === "error") {
				onError();
			}
			// status === "loading" falls through to the listeners below.
		} else {
			const created = document.createElement("script");
			created.src = url;
			created.async = true;
			created.dataset.status = "loading";
			created.addEventListener("load", () => {
				created.dataset.status = "ready";
			});
			created.addEventListener("error", () => {
				created.dataset.status = "error";
			});
			document.body.appendChild(created);
			script = created;
		}

		// This instance's own listeners — fire on the eventual load/error even for a still-loading tag.
		script.addEventListener("load", onLoad);
		script.addEventListener("error", onError);

		return () => {
			cancelled = true;
			script?.removeEventListener("load", onLoad);
			script?.removeEventListener("error", onError);
		};
	}, [url, enabled, usesProbe, logGroup, pollIntervalMs, maxAttempts]);

	return isReady;
};
